import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { getConfig } from "utils/env";
import { PickerOverlay } from "filestack-react";
import StandardImage from "components/StandardImage";
import { signAssetPick, signAssetRead } from "utils/image";
import QS from "qs";
import { isImageMimetype } from "utils/attachment";
import { formFactorAction } from "utils/view";
import UploadError from "./UploadError";
import UploadPrompt from "./UploadPrompt";
import AssetMimetypePrompt from "./AssetMimetypePrompt";
import isError from "lodash/isError";

/**
 * PickerOptions: https://filestack.github.io/filestack-js/interfaces/pickeroptions.html
 * ClientOptions: https://filestack.github.io/filestack-js/interfaces/clientoptions.html
 * Callbacks: https://www.filestack.com/docs/uploads/pickers/web/#callbacks
 * Policy calls: https://www.filestack.com/docs/security/policies/#calls
 * Processing docs: https://www.filestack.com/docs/api/processing/
 * onUploadDone: {
    "filesUploaded":[
        {
            "filename":"myfile.png",
            "handle":"AFrHW1QRsWxmu5ZLU2qg",
            "mimetype":"image/png",
            "originalPath":"picker_transformation.png",
            "size":1277297,
            "source":"local_file_system",
            "url":"https://cdn.filestackcontent.com/AFrHW1QRsWxmu5ZLU2qg",
            "uploadId":"cfcc198e63b7328c17f09f1af519fcdf",
            "originalFile":{
              "name":"myfile",
              "type":"image/png",
              "size":1277297
            },
            "status":"Stored"
          }
        ],
        "filesFailed":[]
      }
      */

//  NOTE: https://stackoverflow.com/questions/59532070/empty-picture-on-checkout-stripe
const FilePicker = ({
  assetPick,
  assetRead,
  headers,
  src,
  onPickerDone,
  pickerOptions,
  prompts,
  icon,
  mimetype,
  size,
  filename,
  image
}) => {
  const [showPicker, setShowPicker] = useState(false);
  const [security, setSecurity] = useState(null);
  const [error, setError] = useState(null);

  const { maxSize } = pickerOptions;
  const ctxSignAssetPick = assetPick || signAssetPick;
  const ctxSignAssetRead = assetRead || signAssetRead;

  const fetchImageSign = async () => {
    const pickCreds = await ctxSignAssetPick(headers);
    if (isError(pickCreds)) {
      setError(pickCreds);
    } else {
      setSecurity(pickCreds);
      if (error) {
        setError(null);
      }
    }
  };

  useEffect(() => {
    !security && fetchImageSign();
  }, []);

  const pickerProps = {
    apikey: getConfig("FILEPICKER_KEY"),
    /**
     * ClientOptions: https://filestack.github.io/filestack-js/interfaces/clientoptions.html
     */
    clientOptions: {
      security
    },
    /**
     * PickerOptions: https://filestack.github.io/filestack-js/interfaces/pickeroptions.html
     */
    pickerOptions: {
      ...pickerOptions,
      onClose: () => {
        showPicker && setShowPicker(false);
      },
      onCancel: () => {
        showPicker && setShowPicker(false);
      },
      onUploadDone: async ({ filesUploaded }) => {
        /**
         * BAD: Move sign calls to the server
         */
        const secureFileUploads = await Promise.all(
          filesUploaded.map(async (upload) => {
            const handleSecurity = await ctxSignAssetRead(
              {
                handle: upload.handle
              },
              headers
            );
            const secureUrl = `${upload.url}?${QS.stringify(handleSecurity)}`;

            return {
              ...upload,
              url: secureUrl
            };
          })
        );

        onPickerDone(secureFileUploads);

        showPicker && setShowPicker(false);
      }
    }
  };

  let content;
  if (error) {
    content = <UploadError prompt={prompts.error} onClick={fetchImageSign} />;
  } else if (src) {
    const isImage = isImageMimetype(mimetype);
    const imageWrapperProps = {
      className: "w-100"
    };
    if (security) {
      imageWrapperProps.className = `${imageWrapperProps.className} pointer bg-white--hover`;
      imageWrapperProps.onClick = () => setShowPicker(true);
    }
    const imageProps = {
      src,
      ...image
    };

    content = (
      <div {...imageWrapperProps}>
        {isImage ? (
          <StandardImage {...imageProps} />
        ) : (
          <AssetMimetypePrompt
            prompt={{
              header: `${formFactorAction()} to update file`
            }}
            filename={filename}
            size={size}
            mimetype={mimetype}
          />
        )}
      </div>
    );
  } else if (security) {
    content = (
      <UploadPrompt
        icon={icon}
        prompt={prompts.upload}
        maxSize={maxSize}
        onClick={() => setShowPicker(true)}
      />
    );
  }

  return (
    <div className="w-100 relative">
      {content}
      {showPicker &&
        security &&
        ReactDOM.createPortal(
          <PickerOverlay {...pickerProps} />,
          document.body
        )}
    </div>
  );
};

export default FilePicker;
